import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Users, CalendarClock, Bell, ClipboardList, ArrowRight, Mic, CheckCircle2, Clock, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

export default function HomeDashboard({ onOpenConsult, onGoTab, onWatchDemo, active }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  const load = () => api.get("/home/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) load(); }, [active]);

  const complete = async (pid) => {
    await api.post(`/followups/${pid}/complete`);
    toast.success("Follow-up marked complete");
    load();
  };

  if (!data) return <div className="h-64 flex items-center justify-center text-slate-400">Loading your cockpit…</div>;

  const stats = [
    { label: t("total_patients"), value: data.stats.total_patients, icon: Users, color: "#1E5AA8", bg: "#EBF3FB" },
    { label: t("consulted_today"), value: data.stats.consulted_today, icon: ClipboardList, color: "#059669", bg: "#ECFDF5" },
    { label: t("followups_due_today"), value: data.stats.due_today, icon: Bell, color: "#F59E0B", bg: "#FFFBEB" },
    { label: t("upcoming_followups"), value: data.stats.upcoming, icon: CalendarClock, color: "#7C3AED", bg: "#F5F3FF" },
  ];

  return (
    <div className="space-y-6" data-testid="home-dashboard">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#1E5AA8] to-[#123C73] p-6 sm:p-8 text-white">
        <div className="absolute -top-12 -right-8 w-56 h-56 rounded-full bg-amber-300/20 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-amber-200 text-xs font-semibold uppercase tracking-wider">MDvia Care Clinical Suite</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1">{t("ready_title")}</h2>
            <p className="text-blue-100 text-sm mt-2 max-w-lg">{t("ready_sub")}</p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-2.5">
            <button data-testid="home-start-consult" onClick={() => onOpenConsult(null)}
              className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#123C73] font-semibold px-5 py-3 rounded-xl transition-colors shadow-lg">
              <Mic className="w-5 h-5" /> {t("start_live_consult")}
            </button>
            <button data-testid="home-watch-demo" onClick={onWatchDemo}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-xl transition-colors ring-1 ring-white/30">
              <PlayCircle className="w-5 h-5" /> {t("watch_demo")}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.bg }}>
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Follow-up reminders */}
        <div className="rounded-2xl bg-white border border-amber-100 shadow-sm overflow-hidden" data-testid="home-followups">
          <div className="flex items-center gap-2 px-5 py-4 bg-[#FFFBEB] border-b border-amber-100">
            <Bell className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="font-semibold text-slate-800">{t("gentle_reminders")}</h3>
          </div>
          <div className="p-4 space-y-3 max-h-96 overflow-auto">
            {data.due_today.length === 0 && data.upcoming.length === 0 && (
              <p className="text-sm text-slate-400 py-8 text-center">No follow-ups scheduled. All caught up ✨</p>
            )}
            {data.due_today.map((p) => (
              <ReminderRow key={p.patient_id} p={p} today onComplete={complete} onOpenConsult={onOpenConsult} />
            ))}
            {data.upcoming.map((p) => (
              <ReminderRow key={p.patient_id} p={p} onComplete={complete} onOpenConsult={onOpenConsult} />
            ))}
          </div>
        </div>

        {/* Recent consultations */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">{t("recent_consultations")}</h3>
            <button onClick={() => onGoTab("followups")} className="text-xs text-[#1E5AA8] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
              {t("view_all")} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-auto">
            {data.recent.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No consultations yet.</p>}
            {data.recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.patient?.name || "Patient"}</p>
                  <p className="text-xs text-slate-400 truncate">
                    <span className="font-mono-id">{r.patient_id}</span> · {(r.chief_complaints || [])[0] || "—"}
                  </p>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{(r.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReminderRow({ p, today, onComplete, onOpenConsult }) {
  return (
    <div className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${today ? "bg-amber-50/60 border-amber-200" : "bg-white border-slate-100"}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
          {today ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F59E0B] text-white font-semibold">TODAY</span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{p.next_follow_up}</span>
          )}
        </div>
        <p className="text-xs text-slate-400 font-mono-id truncate">{p.patient_id}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onOpenConsult(p.patient_id)} title="Continue consultation"
          className="p-2 rounded-lg hover:bg-[#EBF3FB] text-[#1E5AA8]"><Mic className="w-4 h-4" /></button>
        <button onClick={() => onComplete(p.patient_id)} title="Mark done"
          className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
